import axios from "axios";
import { msalInstance } from "../index";
import { urlPath } from "../Constants/defaults";
import { groupType, HistoricalTsiDataType, machineType, TransferMachineInput } from "../Types/portalTypes";
import { MachineDocument, NewGroupDocument } from "../Types/dashboardTypes";
import { MachineDataType } from "../themeContext/MachineDataContext";
import { MachineDetails } from "../Components/Settings/Machines";

export const getIdToken = async (): Promise<string> => {
  const account = msalInstance.getActiveAccount();
  if (!account) {
    // msalInstance.loginRedirect();
    throw Error("No active account! Verify a user has been signed in and setActiveAccount has been called.");
  }
  const response = await msalInstance.acquireTokenSilent({
    scopes: ["openid"],
    account: account,
  });
  return response.idToken;
};

export const getUserDetails = async (): Promise<any> => {
  const idToken = await getIdToken();
  const bearer = `Bearer ${idToken}`;
  const headers = { Authorization: bearer };
  return axios.get(urlPath + "/api/user-details", { headers: headers }).then(function (response) {
    if (response.status == 200) {
      return response.data;
    } else {
      throw new Error();
    }
  });
};

export const postUserDetails = async (object: { [key: string]: string }): Promise<any> => {
  const idToken = await getIdToken();
  const bearer = `Bearer ${idToken}`;
  const headers = { Authorization: bearer };
  return axios.post(urlPath + "/api/user-details", object, { headers: headers }).then(function (response) {
    if (response.status == 200) {
      return response;
    } else {
      throw new Error();
    }
  });
};

export const getGroupDocumentApi = async (): Promise<NewGroupDocument[]> => {
  const idToken = await getIdToken();
  const bearer = `Bearer ${idToken}`;
  const headers = { Authorization: bearer };
  return axios.get(urlPath + "/api/group", { headers: headers }).then(function (response) {
    if (response.status == 200) {
      return response.data;
    } else {
      throw new Error();
    }
  });
};

export const createGroupDocumentApi = async (groupObject: groupType): Promise<NewGroupDocument> => {
  const idToken = await getIdToken();
  const bearer = `Bearer ${idToken}`;
  const headers = { Authorization: bearer };
  return axios
    .post(urlPath + "/api/group", groupObject, { headers: headers })
    .then((res) => {
      if (res.status == 200) {
        return res.data;
      } else {
        throw new Error();
      }
    })
    .catch((error) => {
      throw new Error(error);
    });
};

export const getMachineDocumentsApi = async (): Promise<(NewGroupDocument | MachineDocument)[]> => {
  const idToken = await getIdToken();
  const bearer = `Bearer ${idToken}`;
  const headers = { Authorization: bearer };
  return axios({
    method: "get",
    url: urlPath + "/api/machines",
    timeout: 1000 * 10, // Wait for 10 seconds if this gets stuck in "pending"
    headers: headers,
  }).then(function (response) {
    if (response.status == 200) {
      return response.data;
    } else {
      throw new Error();
    }
  });
};
export const createMachineApi = async (machineObject: machineType): Promise<number> => {
  const idToken = await getIdToken();
  const bearer = `Bearer ${idToken}`;
  const headers = { Authorization: bearer };
  return axios
    .post(urlPath + "/api/machine", machineObject, { headers: headers })
    .then((res) => {
      if (res.status == 200) {
        return res.status;
      } else {
        throw new Error();
      }
    })
    .catch((error) => {
      throw new Error(error);
    });
};

export const renameGroupApi = async (groupName: string, newGroupName: string): Promise<string> => {
  const idToken = await getIdToken();
  const bearer = `Bearer ${idToken}`;
  const headers = { Authorization: bearer };
  return axios
    .patch(urlPath + "/api/group", { newName: newGroupName, oldName: groupName }, { headers: headers })
    .then((res) => {
      if (res.status == 200) {
        return res.data;
      } else {
        throw new Error(res.data);
      }
    })
    .catch((error) => {
      throw new Error(error);
    });
};

export const deleteGroupApi = async (groupName: string): Promise<string> => {
  const idToken = await getIdToken();
  const bearer = `Bearer ${idToken}`;
  const headers = { Authorization: bearer };
  return axios
    .delete(urlPath + "/api/group" + "/" + groupName, { headers: headers })
    .then((res) => {
      if (res.status == 200) {
        return res.data;
      } else {
        throw new Error();
      }
    })
    .catch((error) => {
      throw new Error(error);
    });
};
export const TransferMachineApi = async (transferInput: TransferMachineInput): Promise<string> => {
  const idToken = await getIdToken();
  const bearer = `Bearer ${idToken}`;
  const headers = { Authorization: bearer };
  return axios
    .post(urlPath + "/api/group/transfer", transferInput, { headers: headers })
    .then((res) => {
      if (res.status == 200) {
        return res.data;
      } else {
        throw new Error();
      }
    })
    .catch((error) => {
      throw new Error(error);
    });
};

export const getConfigApi = async (machineSerial: string): Promise<string> => {
  const idToken = await getIdToken();
  const bearer = `Bearer ${idToken}`;
  const headers = { Authorization: bearer };
  return axios
    .get(urlPath + "/api/machine/" + machineSerial + "/config", { headers: headers })
    .then((res) => {
      if (res.status == 200) {
        return res.data;
      } else {
        throw new Error();
      }
    })
    .catch((error) => {
      throw new Error(error);
    });
};

export const renameMachineApi = async (machineData: MachineDocument, newMachineName: string): Promise<string> => {
  const idToken = await getIdToken();
  const bearer = `Bearer ${idToken}`;
  const headers = { Authorization: bearer };
  return axios
    .patch(
      urlPath + "/api/machine",
      { newName: newMachineName, machineId: machineData.serialNumber },
      { headers: headers }
    )
    .then((res) => {
      if (res.status == 200) {
        return res.data;
      } else {
        throw new Error(res.data);
      }
    })
    .catch((error) => {
      throw new Error(error);
    });
};

export const deletMachineApi = async (machineSerial: string): Promise<string> => {
  const idToken = await getIdToken();
  const bearer = `Bearer ${idToken}`;
  const headers = { Authorization: bearer };
  return axios
    .delete(urlPath + "/api/machine" + "/" + machineSerial, { headers: headers })
    .then((res) => {
      if (res.status == 200) {
        return res.data;
      } else {
        throw new Error();
      }
    })
    .catch((error) => {
      throw new Error(error);
    });
};

export const getCurrentTSDataApi = async (machineIds: string[]): Promise<MachineDataType> => {
  const idToken = await getIdToken();
  const bearer = `Bearer ${idToken}`;
  const headers = { Authorization: bearer };
  const start = new Date(new Date().setDate(new Date().getDate() - 30)).toISOString();
  const end = new Date(new Date().setDate(new Date().getDate() + 1)).toISOString();

  const getUrlPath = `${urlPath}/api/current-ts-data?machineIds=${machineIds}&start=${start}&end=${end}`;
  return axios.get(getUrlPath, { headers: headers }).then(function (response) {
    if (response.status == 200) {
      return response.data;
    } else {
      throw new Error();
    }
  });
};

export const getHistoricalTSDataApi = async (machineIds: string[]): Promise<HistoricalTsiDataType> => {
  const idToken = await getIdToken();
  const bearer = `Bearer ${idToken}`;
  const headers = { Authorization: bearer };
  const start = new Date(new Date().setDate(new Date().getDate() - 31)).toISOString();
  const end = new Date(new Date().setDate(new Date().getDate() + 1)).toISOString();
  const getUrlPath = `${urlPath}/api/historical-ts-data?machineIds=${machineIds}&start=${start}&end=${end}`;
  return axios({
    method: "get",
    url: getUrlPath,
    timeout: 1000 * 10, // Wait for 10 seconds if this gets stuck in "pending"
    headers: headers,
  }).then(function (response) {
    if (response.status == 200) {
      return response.data;
    } else {
      throw new Error();
    }
  });
};

export const getMachineDetailsApi = async (groupName: string): Promise<MachineDetails[]> => {
  const idToken = await getIdToken();
  const bearer = `Bearer ${idToken}`;
  const headers = { Authorization: bearer };
  const Path = `${urlPath}/api/machines/group/${groupName}`;
  return axios.get(Path, { headers: headers }).then(function (response) {
    if (response.status == 200) {
      return response.data;
    } else {
      throw new Error();
    }
  });
};
